import React from 'react';
import PropTypes from 'prop-types';

/**
 * Universal DataTable component following the articlesTable.js styling pattern
 * 
 * @param {Object} props
 * @param {Array} props.columns - Array of column definitions
 * @param {Array} props.data - Array of data objects
 * @param {Function} props.onRowClick - Optional row click handler
 * @param {boolean} props.loading - Loading state
 * @param {React.Component} props.SkeletonRow - Skeleton component for loading state
 * @param {React.Component} props.EmptyState - Empty state component
 * @param {boolean} props.selectable - Whether rows are selectable
 * @param {Set} props.selectedItems - Set of selected item IDs
 * @param {Function} props.onSelectItem - Handler for item selection
 * @param {Function} props.onSelectAll - Handler for select all
 * @param {string} props.className - Additional CSS classes
 * @param {Object} props.tableProps - Additional props for the table element
 */
const DataTable = ({
  columns = [],
  data = [],
  onRowClick,
  loading = false,
  SkeletonRow,
  EmptyState,
  selectable = false,
  selectedItems = new Set(),
  onSelectItem,
  onSelectAll,
  className = '',
  tableProps = {},
  itemsPerPageSkeleton = 5,
  ...rest
}) => {
  const renderTableHeader = () => (
    <thead className="bg-white">
      <tr>
        {selectable && (
          <th scope="col" className="py-3.5 !pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-0">
            <div className="flex items-center">
              <input
                type="checkbox"
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                checked={data.length > 0 && selectedItems.size === data.length}
                onChange={onSelectAll}
                ref={(el) => {
                  if (el) {
                    el.indeterminate = selectedItems.size > 0 && selectedItems.size < data.length;
                  }
                }}
              />
              <span className="ml-4">{columns[0]?.title || ''}</span>
            </div>
          </th>
        )}
        {columns.map((column, index) => {
          // Skip first column if selectable (it's handled above)
          if (selectable && index === 0) return null;
          
          return (
            <th
              key={column.key || index}
              scope="col"
              className={column.headerClassName || "px-3 py-3.5 text-left text-sm font-semibold text-gray-900"}
              style={column.headerStyle}
            >
              {column.title}
            </th>
          );
        })}
      </tr>
    </thead>
  );

  const renderTableBody = () => {
    if (loading && SkeletonRow) {
      return (
        <tbody>
          {Array.from({ length: itemsPerPageSkeleton }).map((_, index) => (
            <SkeletonRow key={index} />
          ))}
        </tbody>
      );
    }

    if (!loading && data.length === 0 && EmptyState) {
      return (
        <tbody>
          <tr>
            <td colSpan={columns.length + (selectable ? 1 : 0)} className="p-0">
              <EmptyState />
            </td>
          </tr>
        </tbody>
      );
    }

    return (
      <tbody className="bg-white divide-y divide-gray-200">
        {data.map((item, index) => {
          const isSelected = selectedItems.has(item.id || item.link || index);
          
          return (
            <tr
              key={item.id || item.link || index}
              className={`${
                onRowClick && !loading
                  ? 'hover:bg-neutral-50 cursor-pointer'
                  : ''
              } ${item.className || ''}`}
              onClick={(e) => {
                if (e.target.type === 'checkbox') return;
                if (onRowClick && !loading) {
                  onRowClick(item);
                }
              }}
            >
              {selectable && (
                <td className="whitespace-nowrap py-3 pl-4 pr-3 text-sm sm:pl-0">
                  <div className="flex items-center ml-4">
                    <input
                      type="checkbox"
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      checked={isSelected}
                      onChange={() => onSelectItem && onSelectItem(item.id || item.link || index)}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <div className="ml-4">
                      {columns[0]?.render ? columns[0].render(item) : item[columns[0]?.key]}
                    </div>
                  </div>
                </td>
              )}
              {columns.map((column, colIndex) => {
                // Skip first column if selectable (it's handled above)
                if (selectable && colIndex === 0) return null;
                
                return (
                  <td
                    key={column.key || colIndex}
                    className={column.cellClassName || "whitespace-nowrap px-3 py-5 text-sm text-gray-500"}
                    style={column.cellStyle}
                  >
                    {column.render ? column.render(item) : item[column.key]}
                  </td>
                );
              })}
            </tr>
          );
        })}
      </tbody>
    );
  };

  return (
    <div className={`divide-y divide-gray-100 mt-6 ${className}`} {...rest}>
      <div className="markdown-table-container overflow-x-auto sm:-mx-6 lg:-mx-8">
        <div className="py-2 inline-block min-w-full sm:px-6 lg:px-8">
          <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
            <table 
              className="min-w-full divide-y divide-gray-300" 
              {...tableProps}
            >
              {renderTableHeader()}
              {renderTableBody()}
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

DataTable.displayName = 'DataTable';

DataTable.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string,
    title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    render: PropTypes.func,
    headerClassName: PropTypes.string,
    cellClassName: PropTypes.string,
    headerStyle: PropTypes.object,
    cellStyle: PropTypes.object
  })),
  data: PropTypes.array,
  onRowClick: PropTypes.func,
  loading: PropTypes.bool,
  SkeletonRow: PropTypes.elementType,
  EmptyState: PropTypes.elementType,
  selectable: PropTypes.bool,
  selectedItems: PropTypes.instanceOf(Set),
  onSelectItem: PropTypes.func,
  onSelectAll: PropTypes.func,
  className: PropTypes.string,
  tableProps: PropTypes.object,
  itemsPerPageSkeleton: PropTypes.number
};

export default DataTable;
